import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CodeEditorModule } from '@memberjunction/ng-code-editor';
import { QuillModule } from 'ngx-quill';
import { TimelineModule } from '@memberjunction/ng-timeline';
import { MJFilterPopoverComponent, MJDropdownComponent, MJViewToggleComponent } from '@memberjunction/ng-ui-components';

// UI components
import { IzzyMessageApprovalResource } from './components/message-approval/message-approval.component';
import { IzzyMessagesContainerResource } from './components/messages-container/messages-container.component';
import { SettingsComponent } from './components/settings/settings.component';
import { TicketQueueComponent } from './components/tickets/ticket-queue/ticket-queue.component';
import { TicketDetailComponent } from './components/tickets/ticket-detail/ticket-detail.component';
import { TicketTeamsComponent } from './components/ticket-teams/ticket-teams.component';
import { SLAPoliciesComponent } from './components/sla-policies/sla-policies.component';
import { TicketNumberingComponent } from './components/ticket-numbering/ticket-numbering.component';
import { AssignmentRulesComponent } from './components/assignment-rules/assignment-rules.component';
import { SkillsComponent } from './components/skills/skills.component';
import { AutomationRulesComponent } from './components/automation-rules/automation-rules.component';
import { MacrosComponent } from './components/macros/macros.component';
import { TicketingAdminComponent } from './components/ticketing-admin/ticketing-admin.component';
import { IzzyTicketWorkspaceResource } from './components/tickets/ticket-workspace/ticket-workspace.component';
import { OrganizationsComponent } from './components/organizations/organizations.component';
import { ChannelActionsComponent } from './components/channel-actions/channel-actions.component';
import { APIKeysComponent } from './components/api-keys/api-keys.component';

// Action Governance UI components (v2.0)
import { ActionGalleryComponent } from './components/action-gallery/action-gallery.component';
import { OrganizationActionsComponent } from './components/organization-actions/organization-actions.component';
import { ChannelTypeActionsComponent } from './components/channel-type-actions/channel-type-actions.component';

// Core entity management components (v2.0)
import { ContactsComponent } from './components/contacts/contacts.component';
import { ContactRolesComponent } from './components/contact-roles/contact-roles.component';
import { OrganizationMembersComponent } from './components/organization-members/organization-members.component';
import { PlansComponent } from './components/plans/plans.component';
import { ChannelTypesComponent } from './components/channel-types/channel-types.component';
import { ChannelsComponent } from './components/channels/channels.component';
import { ChannelRunsComponent } from './components/channel-runs/channel-runs.component';
import { ScopesComponent } from './components/scopes/scopes.component';
import { APIKeyScopesComponent } from './components/api-key-scopes/api-key-scopes.component';
import { PrivateActionGrantsComponent } from './components/private-action-grants/private-action-grants.component';

// Credentials management components (v2.1)
import { CredentialsComponent } from './components/credentials/credentials.component';
import { CredentialTypesComponent } from './components/credential-types/credential-types.component';

// AI Configuration management (v2.2)
import { AIConfigurationComponent } from './components/ai-configuration/ai-configuration.component';

// Shared UI components
import { OrgSelectorComponent } from './components/org-selector/org-selector.component';
import { GlobalContextComponent } from './components/global-context/global-context.component';
import { ProfileCompletionComponent } from './components/profile-completion/profile-completion.component';

// Message Editor with WYSIWYG and Track Changes (v2.1)
import { MessageEditorComponent } from './components/message-editor/message-editor.component';
import { WritingAssistMenuComponent } from './components/writing-assist-menu/writing-assist-menu.component';

// Diff Viewer for track changes visualization (v2.1)
import { DiffViewerComponent } from './components/diff-viewer/diff-viewer.component';

// Reusable Message Viewer component (v2.1)
import { MessageViewerComponent } from './components/message-viewer/message-viewer.component';

// AI Analysis pane, shared by the Messages and Ticket screens (v4.1)
import { AIAnalysisComponent } from './components/ai-analysis/ai-analysis.component';


// Payload Viewer for AI processing pipeline visualization (v2.3)
import { PayloadViewerComponent } from './components/payload-viewer/payload-viewer.component';

// Unified Administration Console
import { IzzyAdministrationResource } from './components/administration/administration.component';

// Analytics Dashboard
import { AnalyticsComponent, IzzyAnalyticsResource } from './components/analytics/analytics.component';

// Setup Agent for AI-powered KB generation
import { SetupAgentComponent } from './components/setup-agent/setup-agent.component';

// Channel Message Attachment UI (v2.5)
import { MessageAttachmentsComponent } from './components/message-attachments/message-attachments.component';
import { AttachmentDrawerComponent } from './components/message-attachments/attachment-drawer.component';
import { ArtifactsModule } from '@memberjunction/ng-artifacts';

@NgModule({
  declarations: [
    // Top-level resource components
    IzzyMessageApprovalResource,
    IzzyMessagesContainerResource,
    IzzyAdministrationResource,
    IzzyAnalyticsResource,
    IzzyTicketWorkspaceResource,
    // UI components
    AnalyticsComponent,
    SettingsComponent,
    TicketQueueComponent,
    TicketDetailComponent,
    TicketTeamsComponent,
    SLAPoliciesComponent,
    TicketNumberingComponent,
    AssignmentRulesComponent,
    SkillsComponent,
    AutomationRulesComponent,
    MacrosComponent,
    TicketingAdminComponent,
    OrganizationsComponent,
    ChannelActionsComponent,
    APIKeysComponent,
    // Action Governance (v2.0)
    ActionGalleryComponent,
    OrganizationActionsComponent,
    ChannelTypeActionsComponent,
    // Core entity management (v2.0)
    ContactsComponent,
    ContactRolesComponent,
    OrganizationMembersComponent,
    PlansComponent,
    ChannelTypesComponent,
    ChannelsComponent,
    ChannelRunsComponent,
    ScopesComponent,
    APIKeyScopesComponent,
    PrivateActionGrantsComponent,
    // Credentials management (v2.1)
    CredentialsComponent,
    CredentialTypesComponent,
    // AI Configuration (v2.2)
    AIConfigurationComponent,
    // Shared UI components
    OrgSelectorComponent,
    GlobalContextComponent,
    ProfileCompletionComponent,
    // Message Editor (v2.1)
    MessageEditorComponent,
    WritingAssistMenuComponent,
    // Diff Viewer (v2.1)
    DiffViewerComponent,
    // Message Viewer (v2.1)
    MessageViewerComponent,
    // Payload Viewer (v2.3)
    PayloadViewerComponent,
    // AI Analysis pane, shared by Messages + Tickets (v4.1)
    AIAnalysisComponent,
    // Setup Agent
    SetupAgentComponent,
    // Channel Message Attachments (v2.5)
    MessageAttachmentsComponent,
    AttachmentDrawerComponent
  ],
  imports: [
    MJFilterPopoverComponent,
    MJDropdownComponent,
    MJViewToggleComponent,
    CommonModule,
    FormsModule,
    CodeEditorModule,
    QuillModule.forRoot(),
    TimelineModule,
    ArtifactsModule
  ],
  exports: [
    GlobalContextComponent,
    // Top-level resource components
    IzzyMessageApprovalResource,
    IzzyMessagesContainerResource,
    IzzyAdministrationResource,
    IzzyAnalyticsResource,
    IzzyTicketWorkspaceResource,
    // UI components
    AnalyticsComponent,
    SettingsComponent,
    TicketQueueComponent,
    TicketDetailComponent,
    TicketTeamsComponent,
    SLAPoliciesComponent,
    TicketNumberingComponent,
    AssignmentRulesComponent,
    SkillsComponent,
    AutomationRulesComponent,
    MacrosComponent,
    TicketingAdminComponent,
    OrganizationsComponent,
    ChannelActionsComponent,
    APIKeysComponent,
    // Action Governance (v2.0)
    ActionGalleryComponent,
    OrganizationActionsComponent,
    ChannelTypeActionsComponent,
    // Core entity management (v2.0)
    ContactsComponent,
    ContactRolesComponent,
    OrganizationMembersComponent,
    PlansComponent,
    ChannelTypesComponent,
    ChannelsComponent,
    ChannelRunsComponent,
    ScopesComponent,
    APIKeyScopesComponent,
    PrivateActionGrantsComponent,
    // Credentials management (v2.1)
    CredentialsComponent,
    CredentialTypesComponent,
    // AI Configuration (v2.2)
    AIConfigurationComponent,
    // Shared UI components
    OrgSelectorComponent,
    GlobalContextComponent,
    ProfileCompletionComponent,
    // Message Editor (v2.1)
    MessageEditorComponent,
    WritingAssistMenuComponent,
    // Diff Viewer (v2.1)
    DiffViewerComponent,
    // Message Viewer (v2.1)
    MessageViewerComponent,
    // Payload Viewer (v2.3)
    PayloadViewerComponent,
    // AI Analysis pane, shared by Messages + Tickets (v4.1)
    AIAnalysisComponent,
    // Setup Agent
    SetupAgentComponent
  ]
})
export class IzzyUIModule { }
